/**
 * SEOHead - Component for managing page titles and meta tags
 * 
 * Usage:
 * <SEOHead 
 *   title="Dashboard - FiverFlow"
 *   description="Manage your freelance business"
 *   canonical="/dashboard"
 * />
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  noindex?: boolean;
}

export const SEOHead: React.FC<SEOHeadProps> = ({
  title = 'FiverFlow - Freelance Management Platform',
  description = 'Manage your freelance business with FiverFlow. Track clients, orders, invoices, and grow your freelance career.',
  canonical,
  ogImage = '/LogoFiverFlow.png',
  ogType = 'website',
  noindex = false,
}) => {
  const location = useLocation();

  useEffect(() => {
    // Update document title
    document.title = title;

    // Get or create meta tags
    const getOrCreateMetaTag = (name: string, attribute: 'name' | 'property' = 'name'): HTMLMetaElement => {
      const selector = attribute === 'name' 
        ? `meta[${attribute}="${name}"]`
        : `meta[${attribute}="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      return meta;
    };

    // Update or create description
    const descriptionMeta = getOrCreateMetaTag('description');
    descriptionMeta.content = description;

    // Update or create Open Graph tags
    const ogTitle = getOrCreateMetaTag('og:title', 'property');
    ogTitle.content = title;

    const ogDescription = getOrCreateMetaTag('og:description', 'property');
    ogDescription.content = description;

    const ogImageMeta = getOrCreateMetaTag('og:image', 'property');
    ogImageMeta.content = ogImage.startsWith('http') ? ogImage : `${window.location.origin}${ogImage}`;

    const ogTypeMeta = getOrCreateMetaTag('og:type', 'property');
    ogTypeMeta.content = ogType;

    const ogUrl = getOrCreateMetaTag('og:url', 'property');
    ogUrl.content = canonical 
      ? `${window.location.origin}${canonical}`
      : window.location.href;

    // Twitter Card tags
    const twitterCard = getOrCreateMetaTag('twitter:card');
    twitterCard.content = 'summary_large_image';

    const twitterTitle = getOrCreateMetaTag('twitter:title');
    twitterTitle.content = title;

    const twitterDescription = getOrCreateMetaTag('twitter:description');
    twitterDescription.content = description;

    // Robots meta
    const robotsMeta = getOrCreateMetaTag('robots');
    robotsMeta.content = noindex ? 'noindex, nofollow' : 'index, follow';

    // Update or create canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonical 
      ? `${window.location.origin}${canonical}`
      : window.location.href;

  }, [title, description, canonical, ogImage, ogType, noindex, location.pathname]);

  return null; // This component doesn't render anything
};

